import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import googleImage from '../../assets/images/google.png';
import appleImage from '../../assets/images/apple_logo.png';
import darkAppleImage from '../../assets/images-night/apple_logo.png';
import emailIcon from '../../assets/images/email.png';
import padlockIcon from '../../assets/images/padlock.png';
import userIcon from '../../assets/images/user.png';
import { useThemeContext } from '../../contexts/ThemeContext';
import iconLauncher from '../../assets/images/ic_launcher_round.png';
import darkIconLauncher from '../../assets/images-night/ic_launcher_round.png';
import { useAuth } from '../../hooks/useAuth';
import { googleAuth } from '../../utils/googleAuth';

interface SignupFormProps {
  onSignupSuccess?: () => void;
}

interface SignupFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  verificationCode: string;
}

const SignupForm: React.FC<SignupFormProps> = ({ onSignupSuccess }) => {
  const { isDark } = useThemeContext();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<SignupFormData>({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: ''
  });
  const { signup, sendVerificationCode, loginWithGoogle, isLoading, error } = useAuth();
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [verificationTimer, setVerificationTimer] = useState(0);

  // Initialize Google Auth on component mount
  useEffect(() => {
    const initGoogleAuth = async () => {
      try {
        await googleAuth.initialize();
      } catch (err) {
        console.error('Failed to initialize Google Auth:', err);
      }
    };

    initGoogleAuth();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (verificationTimer > 0) {
      interval = setInterval(() => {
        setVerificationTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [verificationTimer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSendVerificationCode = async () => {
    if (!formData.email) {
      toast.error('Please enter your email first');
      return;
    }
    
    const result = await sendVerificationCode(formData.email);
    if (result.success) {
      setIsVerificationSent(true);
      setVerificationTimer(60); // 60 seconds
    }
  };

  const handleGoogleSignup = async () => {
    // Create a container for Google button if it doesn't exist
    let googleButtonContainer = document.getElementById('google-signup-button');
    if (!googleButtonContainer) {
      googleButtonContainer = document.createElement('div');
      googleButtonContainer.id = 'google-signup-button';
      googleButtonContainer.style.display = 'none';
      document.body.appendChild(googleButtonContainer);
    }

    // Render Google button and trigger click
    googleAuth.renderButton('google-signup-button', async (credential: string) => {
      const result = await loginWithGoogle(credential);
      if (result.success) {
        onSignupSuccess?.();
      }
    });

    // Trigger the Google button click
    const googleButton = googleButtonContainer.querySelector('div[role="button"]');
    if (googleButton) {
      (googleButton as HTMLElement).click();
    } else {
      // Fallback: show message to user
      toast.info('Please click the Google button above to sign up');
    }
  };

  const handleAppleSignup = () => {
    toast.info('Apple Sign-In is not available at the moment.');
  };

  const nextStep = () => {
    if (currentStep === 1 && (!formData.username || !formData.email)) {
      toast.error('Please fill in both username and email');
      return;
    }
    
    // Send verification code when moving to step 2
    if (currentStep === 1 && formData.email) {
      handleSendVerificationCode();
    }
    
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Validate verification code
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit verification code');
      return;
    }

    // Check if all fields are filled
    const { username, email, password, verificationCode } = formData;
    if (!username || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    const result = await signup({
      username,
      email,
      password,
      password_confirmation: password,
      verification_code: verificationCode,
      is_google_auth: false,
      firstname: null,
      lastname: null,
      image_url: null
    });

    if (result.success) {
      onSignupSuccess?.();
    }
  };

  const renderStepIndicator = () => (
    <div className="flex justify-center items-center">
      <div className="flex space-x-2">
        {[1, 2].map((step) => (
          <div
            key={step}
            className={`w-3 h-3 rounded-full transition-colors duration-200 ${step === currentStep
                ? 'bg-primary'
                : step < currentStep
                  ? 'bg-primary/50'
                  : 'bg-grey_light'
              }`}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full flex items-center justify-center p-4">
      <div className="max-w-md w-full min-h-64 bg-light p-8 rounded-lg shadow-md">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-medium text-dark">Create Account</h2>
          {renderStepIndicator()}
          <img
            src={isDark ? iconLauncher : darkIconLauncher}
            alt="App Icon"
            style={{ width: 50, height: 50 }}
            className="border rounded-lg border-bg_grey_light"
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Social Login (only on step 1) */}
        {currentStep === 1 && (
          <div className="mb-4">
            <div className="text-dark mb-2">
              Sign Up with Open account
            </div>
            <div className='flex space-x-4 text-sm'>
              <Button 
                variant="google" 
                className="w-full mt-2"
                onClick={handleGoogleSignup}
                disabled={isLoading}
              >
                <img
                  src={googleImage}
                  alt="Google Icon"
                  className="w-5 h-5 mr-2 inline-block align-middle"
                />
                <span className="align-middle text-dark">Google</span>
              </Button>

              <Button 
                variant="google" 
                className="w-full mt-2"
                onClick={handleAppleSignup}
                disabled={isLoading}
              >
                <img
                  src={isDark ? darkAppleImage : appleImage}
                  alt="Apple Icon"
                  className="w-5 h-5 mr-2 inline-block align-middle"
                />
                <span className="align-middle text-dark">Apple</span>
              </Button>
            </div>
          </div>
        )}

        {/* Divider (only on step 1) */}
        {currentStep === 1 && (
          <div className="flex items-center my-2">
            <hr className="flex-grow border-t border-grey_light" />
            <span className="mx-4 text-grey_light">or</span>
            <hr className="flex-grow border-t border-grey_light" />
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Step 1: Account Information (Username + Email) */}
          {currentStep === 1 && (
            <>
              <div className="text-dark mb-4">
                Or continue with email address
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium mb-1 text-dark">
                  Username
                </label>
                <Input
                  leftIcon={userIcon}
                  attributes={{
                    id: "username",
                    name: "username",
                    type: "text",
                    placeholder: "Choose a username",
                    value: formData.username,
                    onChange: handleChange,
                    required: true,
                    disabled: isLoading
                  }}
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1 text-dark">
                  Email Address
                </label>
                <Input
                  leftIcon={emailIcon}
                  attributes={{
                    id: "email",
                    name: "email",
                    type: "email",
                    placeholder: "Enter your email",
                    value: formData.email,
                    onChange: handleChange,
                    required: true,
                    disabled: isLoading
                  }}
                  className="w-full"
                />
              </div>

              <button
                type="button"
                onClick={nextStep}
                disabled={isLoading}
                className="w-full py-2 px-4 rounded-lg font-medium bg-primary hover:bg-primary-variant text-white transition-colors duration-200 disabled:bg-grey_dark disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </>
          )}

          {/* Step 2: Security Settings (Password + Confirm Password + Verification) */}
          {currentStep === 2 && (
            <>
              {/* Verification Code Section */}
              <div className="mb-6 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="verificationCode" className="block text-sm font-medium text-dark">
                    Verification Code
                  </label>
                  {isVerificationSent && verificationTimer > 0 && (
                    <span className="text-sm text-primary">
                      Resend in {verificationTimer}s
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between space-x-2">
                  <Input
                    attributes={{
                      id: "verificationCode",
                      name: "verificationCode",
                      type: "text",
                      placeholder: "Enter 6-digit code",
                      value: formData.verificationCode,
                      onChange: handleChange,
                      maxLength: 6,
                      pattern: "[0-9]{6}",
                      required: true,
                      disabled: isLoading
                    }}
                    className=""
                  />
                  <button
                    type="button"
                    onClick={handleSendVerificationCode}
                    disabled={verificationTimer > 0 || isLoading}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${
                      verificationTimer > 0 || isLoading
                        ? 'bg-grey_light text-grey_dark cursor-not-allowed'
                        : 'bg-primary hover:bg-primary-variant text-white'
                    }`}
                  >
                    {isVerificationSent ? 'Resend' : 'Send Code'}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-1 text-dark">
                  Password
                </label>
                <Input
                  leftIcon={padlockIcon}
                  showPasswordToggle={true}
                  attributes={{
                    id: "password",
                    name: "password",
                    placeholder: "Enter your password",
                    value: formData.password,
                    onChange: handleChange,
                    required: true,
                    disabled: isLoading
                  }}
                  className="w-full"
                  type="password"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1 text-dark">
                  Confirm Password
                </label>
                <Input
                  leftIcon={padlockIcon}
                  showPasswordToggle={true}
                  attributes={{
                    id: "confirmPassword",
                    name: "confirmPassword",
                    placeholder: "Confirm your password",
                    value: formData.confirmPassword,
                    onChange: handleChange,
                    required: true,
                    disabled: isLoading
                  }}
                  className="w-full"
                  type="password"
                />
              </div>

              <div className="flex space-x-4 mt-4">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={isLoading}
                  className="flex-1 py-2 px-4 rounded-lg font-medium border border-grey_light hover:bg-grey_light text-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${
                    isLoading
                      ? 'bg-grey_light cursor-not-allowed text-grey_dark'
                      : 'bg-primary hover:bg-primary-variant text-white'
                    }`}
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            </>
          )}
        </form>

        <div className="text-center mt-3">
          <p className="text-dark">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-primary hover:text-primary-variant font-medium transition-colors duration-200"
            >
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;